import { hasPlatformIcon, resolvePlatformIcon } from '@/common/utils/resolvePlatformIcon'
import { normalizeActionImageName } from '@/services/action/normalizeActionTransfer'

export const TABLE_CONTEXT_MENU_COPY_LABEL = 'Копировать значение'
export const TABLE_ROW_PRIMARY_ACTION_FALLBACK_ICON = 'pi pi-arrow-right'
export const TABLE_ROW_CONTEXT_ACTIONS_COL_ONE_PX = 36
export const TABLE_ROW_CONTEXT_ACTIONS_COL_TWO_PX = 56

export type TableRowContextActionIcon =
  | { kind: 'prime'; className: string }
  | { kind: 'image'; url: string }

export type TableRowContextActionsVisibility = {
  showColumn: boolean
  showOverflow: boolean
}

export type TableRowContextOverride = {
  showPrimary: boolean
  showOverflow: boolean
  icon?: string
  label?: string
}

export type TableRowPrimaryActionPresentation = {
  label: string
  icon: TableRowContextActionIcon
}

function isCopyContextMenuItem(item: unknown): boolean {
  if (!item || typeof item !== 'object') return false
  const label = String((item as { label?: unknown }).label ?? '').trim()
  return label === TABLE_CONTEXT_MENU_COPY_LABEL
}

/** Платформенные пункты ПКМ: без клиентского «Копировать значение». */
export function filterPlatformContextMenuItems<T>(items: T[] | null | undefined): T[] {
  if (!Array.isArray(items)) return []
  return items.filter((item) => !isCopyContextMenuItem(item))
}

export function resolveTableRowContextActionsVisibility(
  platformItems: unknown[] | null | undefined,
): TableRowContextActionsVisibility {
  const items = filterPlatformContextMenuItems(platformItems)
  return {
    showColumn: items.length > 0,
    showOverflow: items.length > 1,
  }
}

/** Снимок кнопок строки после onInlineMenuShow (в т.ч. 0 пунктов → скрыть кнопку). */
export function resolveTableRowContextOverride(
  items: unknown[] | null | undefined,
): TableRowContextOverride {
  const platform = filterPlatformContextMenuItems(items)
  const first = platform[0] as { label?: string; icon?: string } | undefined
  return {
    showPrimary: platform.length > 0,
    showOverflow: platform.length > 1,
    icon: first?.icon,
    label: first?.label,
  }
}

export function tableRowContextOverridesEqual(
  a: TableRowContextOverride | undefined,
  b: TableRowContextOverride,
): boolean {
  if (!a) return false
  return (
    a.showPrimary === b.showPrimary &&
    a.showOverflow === b.showOverflow &&
    a.icon === b.icon &&
    a.label === b.label
  )
}

/** Preview onInlineMenuShow: один проход на ключ, очередь (deferred vis общий). */
export function createTableRowContextPreviewGate() {
  const pending = new Set<string>()
  let chain: Promise<void> = Promise.resolve()
  return {
    enqueue(key: string, task: () => Promise<void>): void {
      const id = String(key ?? '').trim()
      if (!id || pending.has(id)) return
      pending.add(id)
      chain = chain
        .then(async () => {
          await task()
        })
        .catch(() => undefined)
        .finally(() => {
          pending.delete(id)
        })
    },
  }
}

export function resolveTableRowContextActionsColumnWidthPx(showOverflow: boolean): number {
  return showOverflow ? TABLE_ROW_CONTEXT_ACTIONS_COL_TWO_PX : TABLE_ROW_CONTEXT_ACTIONS_COL_ONE_PX
}

function readContextMenuItemIconName(item: unknown): string | undefined {
  if (!item || typeof item !== 'object') return undefined
  const rec = item as { icon?: unknown; image?: unknown }
  return normalizeActionImageName(rec.icon) ?? normalizeActionImageName(rec.image)
}

function readContextMenuItemLabel(item: unknown): string {
  if (!item || typeof item !== 'object') return ''
  const rec = item as { label?: unknown; title?: unknown }
  const label = String(rec.label ?? '').trim()
  if (label) return label
  return String(rec.title ?? '').trim()
}

export function resolveTableRowPrimaryActionPresentation(
  item: unknown | null | undefined,
): TableRowPrimaryActionPresentation {
  const label = readContextMenuItemLabel(item)
  const iconName = readContextMenuItemIconName(item)
  if (!iconName || !hasPlatformIcon(iconName)) {
    return {
      label,
      icon: { kind: 'prime', className: TABLE_ROW_PRIMARY_ACTION_FALLBACK_ICON },
    }
  }
  const resolved = resolvePlatformIcon(iconName)
  if (resolved.kind === 'image') {
    return { label, icon: { kind: 'image', url: resolved.url } }
  }
  return { label, icon: { kind: 'prime', className: resolved.primeClass } }
}
